import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, RefreshCw, Loader2, Plus, Pencil, Trash2, PackageCheck, Store, AlertTriangle, MapPin, FlaskConical, ExternalLink, Wand2, Scale,
} from "lucide-react";

const EMPTY_RULE = {
  rule_name: "", wc_shipping_method: "", destination_zone: "", postcode_rule: "",
  min_weight: "", max_weight: "", max_length: "", max_width: "", max_height: "",
  min_order_value: "", max_order_value: "", carrier: "", carrier_service: "",
  rm_service_code: "", rm_service_register_code: "", rm_package_format: "parcel",
  auto_label: false, priority: 100, enabled: true,
};

const num = (v) => (v === "" || v == null ? null : Number(v));

const QUEUE_STATES = [
  { key: "all", label: "All" },
  { key: "needs_order", label: "Needs order" },
  { key: "needs_label", label: "Needs label" },
  { key: "labelled", label: "Labelled" },
  { key: "manual_review", label: "Manual review" },
  { key: "no_shipment", label: "No shipment" },
];
const QUEUE_BADGE = {
  needs_order: "bg-indigo-50 text-indigo-700 border-indigo-200",
  needs_label: "bg-amber-50 text-amber-700 border-amber-200",
  labelled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  manual_review: "bg-red-50 text-red-700 border-red-200",
  no_shipment: "bg-violet-50 text-violet-700 border-violet-200",
};
const QUEUE_LABEL = Object.fromEntries(QUEUE_STATES.map((s) => [s.key, s.label]));
const QueueBadge = ({ state }) => <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${QUEUE_BADGE[state] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{QUEUE_LABEL[state] || state}</span>;

export default function ShippingSettings() {
  const { user, isSuper, can } = useAuth();
  const canEdit = isSuper || can("settings.manage");
  const [rules, setRules] = useState([]);
  const [zones, setZones] = useState([]);
  const [preview, setPreview] = useState([]);
  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState("all");
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueLoaded, setQueueLoaded] = useState(false);
  const [enrichStatus, setEnrichStatus] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editing, setEditing] = useState(null); // rule object or null

  const loadRules = useCallback(() => api.get("/shipping/rules").then((r) => setRules(r.data.rules || [])).catch(() => {}), []);
  const loadZones = useCallback(() => api.get("/shipping/zones").then((r) => setZones(r.data.zones || [])).catch(() => {}), []);
  const loadQueue = useCallback(() => {
    setQueueLoading(true);
    return api.get("/shipping/fulfilment-queue", { params: { limit: 100 } })
      .then((r) => { setQueue(r.data.items || []); setQueueLoaded(true); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load queue"))
      .finally(() => setQueueLoading(false));
  }, []);
  const loadEnrichStatus = useCallback(() => api.get("/shipping/enrichment-status").then((r) => setEnrichStatus(r.data)).catch(() => {}), []);
  const [inferStatus, setInferStatus] = useState(null);
  const [inferring, setInferring] = useState(false);
  const loadInferStatus = useCallback(() => api.get("/shipping/title-inference-status").then((r) => setInferStatus(r.data)).catch(() => {}), []);
  const inferNow = async () => {
    setInferring(true);
    try {
      const r = await api.post("/shipping/infer-weights-from-titles");
      const rc = r.data.recompute;
      toast.success(`Scanned ${r.data.scanned} · populated ${r.data.populated} · skipped ${r.data.skipped_existing} · ambiguous ${r.data.ambiguous}${rc ? ` · ${rc.orders_with_weight} orders now weighted` : ""}`);
      await Promise.all([loadQueue(), loadInferStatus()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Inference failed"); }
    finally { setInferring(false); }
  };
  const enrichNow = async () => {
    setEnriching(true);
    try { const r = await api.post("/shipping/enrich-products"); toast.success(`Checked ${r.data.checked} · updated ${r.data.updated} · API fails ${r.data.api_failures}`); await Promise.all([loadQueue(), loadEnrichStatus()]); }
    catch (e) { toast.error(e.response?.data?.detail || "Enrichment failed"); }
    finally { setEnriching(false); }
  };

  useEffect(() => { Promise.all([loadRules(), loadZones()]).finally(() => setLoading(false)); }, [loadRules, loadZones]);

  const syncZones = async () => {
    setSyncing(true);
    try { const r = await api.post("/shipping/zones/sync"); toast.success(`Synced ${r.data.zones} WooCommerce zones`); await loadZones(); }
    catch (e) { toast.error(e.response?.data?.detail || "Zone sync failed"); }
    finally { setSyncing(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try { const r = await api.get("/shipping/preview-sample", { params: { limit: 15 } }); setPreview(r.data.items || []); toast.success(`Resolved ${r.data.items.length} orders`); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const saveRule = async () => {
    const payload = {
      rule_name: editing.rule_name, wc_shipping_method: editing.wc_shipping_method || null,
      destination_zone: editing.destination_zone || null, postcode_rule: editing.postcode_rule || null,
      min_weight: num(editing.min_weight), max_weight: num(editing.max_weight),
      max_length: num(editing.max_length), max_width: num(editing.max_width), max_height: num(editing.max_height),
      min_order_value: num(editing.min_order_value), max_order_value: num(editing.max_order_value),
      carrier: editing.carrier || null, carrier_service: editing.carrier_service || null,
      rm_service_code: editing.rm_service_code || null, rm_service_register_code: editing.rm_service_register_code || null,
      rm_package_format: editing.rm_package_format || null,
      auto_label: !!editing.auto_label, priority: Number(editing.priority) || 100, enabled: !!editing.enabled,
    };
    if (!payload.rule_name) { toast.error("Rule name is required"); return; }
    try {
      if (editing.id) await api.put(`/shipping/rules/${editing.id}`, payload);
      else await api.post("/shipping/rules", payload);
      toast.success("Rule saved");
      setEditing(null);
      await loadRules();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rule"); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this shipping rule?")) return;
    try { await api.delete(`/shipping/rules/${id}`); toast.success("Rule deleted"); await loadRules(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete rule"); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading shipping…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="shipping-settings-page">
      <div className="flex items-start gap-3 mb-6">
        <span className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Truck className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Shipping &amp; Fulfilment</h1>
          <p className="text-sm text-slate-500 mt-0.5">Translate WooCommerce delivery methods into carrier services. Checkout pricing stays in WooCommerce — this is fulfilment only.</p>
        </div>
      </div>

      <Tabs defaultValue="rules" onValueChange={(v) => { if (v === "queue") { if (!queueLoaded) loadQueue(); loadEnrichStatus(); loadInferStatus(); } }}>
        <TabsList data-testid="shipping-tabs">
          <TabsTrigger value="rules" data-testid="shipping-tab-rules">Rules</TabsTrigger>
          <TabsTrigger value="zones" data-testid="shipping-tab-zones">WooCommerce Zones</TabsTrigger>
          <TabsTrigger value="preview" data-testid="shipping-tab-preview">Preview &amp; Test</TabsTrigger>
          <TabsTrigger value="queue" data-testid="shipping-tab-queue">Fulfilment Queue</TabsTrigger>
        </TabsList>

        {/* RULES */}
        <TabsContent value="rules" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">{rules.length} rule{rules.length === 1 ? "" : "s"} · evaluated by priority (low number = higher priority)</p>
            {canEdit && (
              <Button onClick={() => setEditing({ ...EMPTY_RULE })} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-rule-btn">
                <Plus className="w-4 h-4" /> Add rule
              </Button>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="rules-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  {["Pri", "Rule", "WC method", "Zone", "Postcode", "Weight", "Max dims", "Order £", "Carrier / service", "Label", "On", ""].map((h) => (
                    <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rules.length === 0 && <tr><td colSpan={12} className="text-center text-slate-400 py-8">No shipping rules yet.</td></tr>}
                {rules.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`rule-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono">{r.priority}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.rule_name}</td>
                    <td className="px-3 py-2">{r.wc_shipping_method || "any"}</td>
                    <td className="px-3 py-2">{r.destination_zone || "any"}</td>
                    <td className="px-3 py-2 font-mono">{r.postcode_rule || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.min_weight ?? "0"}–{r.max_weight ?? "∞"} kg</td>
                    <td className="px-3 py-2 whitespace-nowrap">{[r.max_length, r.max_width, r.max_height].map((x) => x ?? "∞").join("×")}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.min_order_value ?? "0"}–{r.max_order_value ?? "∞"}</td>
                    <td className="px-3 py-2"><span className="font-medium text-slate-800">{r.carrier || "—"}</span><span className="text-slate-400"> · {r.carrier_service || "—"}</span></td>
                    <td className="px-3 py-2">{r.auto_label ? <PackageCheck className="w-4 h-4 text-emerald-600" /> : "—"}</td>
                    <td className="px-3 py-2">{r.enabled ? <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> : <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canEdit && (<>
                        <button onClick={() => setEditing({ ...r })} className="text-slate-400 hover:text-indigo-600 p-1" data-testid={`edit-rule-${r.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteRule(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-rule-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ZONES */}
        <TabsContent value="zones" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">Authoritative zones synced from WooCommerce. Postcode/method derivation is only a fallback.</p>
            {canEdit && (
              <Button onClick={syncZones} disabled={syncing} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sync-zones-btn">
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync from WooCommerce
              </Button>
            )}
          </div>
          {zones.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-400" data-testid="zones-empty">
              <MapPin className="w-6 h-6 mx-auto mb-2" /> No zones synced yet.{canEdit ? " Click “Sync from WooCommerce”." : ""}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="zones-list">
              {zones.map((z) => (
                <div key={z.zone_id} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-slate-900">{z.name}</h3>
                    <span className="text-[11px] font-mono text-slate-400">#{z.zone_id}</span>
                  </div>
                  {z.postcodes?.length > 0 && <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-600">Postcodes:</span> {z.postcodes.join(", ")}</p>}
                  {z.countries?.length > 0 && <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-600">Countries:</span> {z.countries.join(", ")}</p>}
                  {z.methods?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {z.methods.map((m, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{m.title || m.method_id}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PREVIEW */}
        <TabsContent value="preview" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">Run the translation engine against recent real WooCommerce orders. Preview only — no Royal Mail calls.</p>
            <Button onClick={runPreview} disabled={previewing} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="run-preview-btn">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Resolve recent orders
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="preview-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>{["Order", "WC method", "Zone (source)", "Postcode", "Weight", "Dims", "Value", "→ Resolved"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Click “Resolve recent orders” to run the engine.</td></tr>}
                {preview.map((p) => {
                  const res = p.resolution || {};
                  const status = res.status || (res.click_collect ? "no_shipment" : res.resolved ? "resolved" : "manual_review");
                  const cc = status === "no_shipment";
                  const ok = status === "resolved";
                  return (
                    <tr key={p.legacy_id} className="hover:bg-slate-50" data-testid={`preview-row-${p.legacy_id}`}>
                      <td className="px-3 py-2 font-mono">#{p.order_reference}</td>
                      <td className="px-3 py-2">{p.wc_method || "—"}</td>
                      <td className="px-3 py-2">{p.zone || "—"} <span className="text-[11px] text-slate-400">{p.zone_source ? `(${p.zone_source})` : ""}</span></td>
                      <td className="px-3 py-2 font-mono">{p.postcode || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{p.parcel_weight_kg != null ? `${p.parcel_weight_kg} kg` : "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{p.parcel_dimensions_cm ? [p.parcel_dimensions_cm.length, p.parcel_dimensions_cm.width, p.parcel_dimensions_cm.height].map((x) => (x ? Math.round(x) : "—")).join("×") : "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{p.order_value != null ? `£${Number(p.order_value).toFixed(0)}` : "—"}</td>
                      <td className="px-3 py-2">
                        {cc ? (
                          <span className="inline-flex items-center gap-1 text-violet-700 font-medium"><Store className="w-3.5 h-3.5" /> Click &amp; Collect (no label)</span>
                        ) : ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><PackageCheck className="w-3.5 h-3.5" /> {res.carrier} · {res.carrier_service}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Manual review — {res.reason || "no rule matched"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* QUEUE */}
        <TabsContent value="queue" className="mt-4">
          {queue.filter((q) => q.state === "manual_review").length > 0 && (
            <div className="flex items-start gap-2 mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="queue-warning-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{queue.filter((q) => q.state === "manual_review").length} orders require product shipping data before labels can be created.</span>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 mb-3" data-testid="enrichment-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Product shipping data</h3>
                <p className="text-xs text-slate-500">{enrichStatus?.last_run ? `Last enriched ${new Date(enrichStatus.last_run).toLocaleString()}` : "Never enriched. OPPS is the source of truth; WooCommerce only fills blank values."}</p>
              </div>
              {canEdit && (
                <Button onClick={enrichNow} disabled={enriching} className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white shrink-0" data-testid="enrich-products-btn">
                  {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Enrich all product weights &amp; dimensions
                </Button>
              )}
            </div>
            {enrichStatus?.totals && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3 text-center text-xs" data-testid="enrichment-totals">
                {[["Checked", "checked"], ["Updated", "updated"], ["Complete", "already_complete"], ["No weight", "missing_weight"], ["No dims", "missing_dims"], ["API fails", "api_failures"]].map(([lab, k]) => (
                  <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-2"><div className="text-slate-400">{lab}</div><div className="font-bold text-slate-900">{enrichStatus.totals[k] ?? 0}</div></div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 mb-3" data-testid="title-inference-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Scale className="w-4 h-4 text-violet-600" /> Infer weights from product titles</h3>
                <p className="text-xs text-slate-500">{inferStatus?.last_run ? `Last run ${new Date(inferStatus.last_run).toLocaleString()}` : "Reads weights/volumes from titles (e.g. \u201cChlorine Granules 5KG\u201d, \u201c2 x 1L\u201d) into blank product weight fields. Never overwrites an existing or manually-set weight."}</p>
              </div>
              {canEdit && (
                <Button onClick={inferNow} disabled={inferring} className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white shrink-0" data-testid="infer-weights-btn">
                  {inferring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Infer weights from titles
                </Button>
              )}
            </div>
            {inferStatus?.summary && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3 text-center text-xs" data-testid="title-inference-totals">
                  {[["Scanned", "scanned"], ["Populated", "populated"], ["Refined", "refined"], ["Skipped (had weight)", "skipped_existing"], ["Ambiguous", "ambiguous"]].map(([lab, k]) => (
                    <div key={k} className="rounded-lg bg-slate-50 border border-slate-100 p-2"><div className="text-slate-400">{lab}</div><div className="font-bold text-slate-900">{inferStatus.summary[k] ?? 0}</div></div>
                  ))}
                </div>
                {inferStatus.summary.by_rule_type && Object.keys(inferStatus.summary.by_rule_type).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Inferred by rule type</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="title-inference-rules">
                      {Object.entries(inferStatus.summary.by_rule_type).sort((a, b) => b[1] - a[1]).map(([rule, n]) => (
                        <span key={rule} className="inline-flex items-center gap-1 text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100 rounded-full px-2.5 py-1">{rule} <span className="font-bold">{n}</span></span>
                      ))}
                    </div>
                  </div>
                )}
                {inferStatus.ambiguous_sample?.length > 0 && (
                  <details className="mt-3 group" data-testid="title-inference-ambiguous">
                    <summary className="text-xs font-semibold text-slate-600 cursor-pointer select-none">Ambiguous / unmatched products to review ({inferStatus.summary.ambiguous})</summary>
                    <div className="mt-2 max-h-52 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
                      {inferStatus.ambiguous_sample.map((p, i) => (
                        <div key={p.legacy_id || i} className="px-3 py-1.5 text-xs text-slate-600 flex items-center gap-2">
                          <span className="text-slate-300 shrink-0">{p.legacy_id}</span>
                          <span className="truncate">{p.title}</span>
                        </div>
                      ))}
                      {inferStatus.summary.ambiguous > inferStatus.ambiguous_sample.length && (
                        <div className="px-3 py-1.5 text-[11px] text-slate-400">Showing first {inferStatus.ambiguous_sample.length} of {inferStatus.summary.ambiguous}. Set weights manually on each product record.</div>
                      )}
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">Every web order and where it is in fulfilment. Manual two-step flow: open an order to create the Royal Mail order, then pull the label.</p>
            <Button onClick={loadQueue} disabled={queueLoading} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="refresh-queue-btn">
              {queueLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="queue-filters">
            {QUEUE_STATES.map((s) => (
              <button key={s.key} onClick={() => setQueueFilter(s.key)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${queueFilter === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`queue-filter-${s.key}`}>
                {s.label} ({queue.filter((q) => s.key === "all" || q.state === s.key).length})
              </button>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="queue-table">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>{["Order", "Recipient", "Postcode", "Service", "Tracking", "State", ""].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {queue.filter((q) => queueFilter === "all" || q.state === queueFilter).length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">{queueLoaded ? "No orders in this state." : "Click Refresh to load the queue."}</td></tr>}
                {queue.filter((q) => queueFilter === "all" || q.state === queueFilter).map((q) => (
                  <tr key={q.legacy_id} className="hover:bg-slate-50" data-testid={`queue-row-${q.legacy_id}`}>
                    <td className="px-3 py-2 font-mono">#{q.order_reference}</td>
                    <td className="px-3 py-2">{q.recipient || "—"}</td>
                    <td className="px-3 py-2 font-mono">{q.postcode || "—"}</td>
                    <td className="px-3 py-2">{q.service || "—"}</td>
                    <td className="px-3 py-2 font-mono">{q.tracking_number || "—"}</td>
                    <td className="px-3 py-2"><QueueBadge state={q.state} /></td>
                    <td className="px-3 py-2 text-right"><Link to={`/tasks/${q.legacy_id}`} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-semibold" data-testid={`queue-open-${q.legacy_id}`}>Open <ExternalLink className="w-3.5 h-3.5" /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="rule-editor-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit rule" : "New shipping rule"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              <F label="Rule name" full><Input value={editing.rule_name} onChange={(e) => setEditing({ ...editing, rule_name: e.target.value })} data-testid="rule-field-name" /></F>
              <F label="WC shipping method (blank = any)"><Input value={editing.wc_shipping_method} onChange={(e) => setEditing({ ...editing, wc_shipping_method: e.target.value })} placeholder="e.g. flat_rate or Next Working Day" data-testid="rule-field-method" /></F>
              <F label="Destination zone (blank = any)"><Input value={editing.destination_zone} onChange={(e) => setEditing({ ...editing, destination_zone: e.target.value })} placeholder="e.g. Scottish Highlands" data-testid="rule-field-zone" /></F>
              <F label="Postcode rule (e.g. PO30* or AB10...AB16)"><Input value={editing.postcode_rule} onChange={(e) => setEditing({ ...editing, postcode_rule: e.target.value })} data-testid="rule-field-postcode" /></F>
              <F label="Priority (low = first)"><Input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} data-testid="rule-field-priority" /></F>
              <F label="Min weight (kg)"><Input type="number" value={editing.min_weight} onChange={(e) => setEditing({ ...editing, min_weight: e.target.value })} /></F>
              <F label="Max weight (kg)"><Input type="number" value={editing.max_weight} onChange={(e) => setEditing({ ...editing, max_weight: e.target.value })} /></F>
              <F label="Max length (cm)"><Input type="number" value={editing.max_length} onChange={(e) => setEditing({ ...editing, max_length: e.target.value })} /></F>
              <F label="Max width (cm)"><Input type="number" value={editing.max_width} onChange={(e) => setEditing({ ...editing, max_width: e.target.value })} /></F>
              <F label="Max height (cm)"><Input type="number" value={editing.max_height} onChange={(e) => setEditing({ ...editing, max_height: e.target.value })} /></F>
              <F label="Min order value (£)"><Input type="number" value={editing.min_order_value} onChange={(e) => setEditing({ ...editing, min_order_value: e.target.value })} /></F>
              <F label="Max order value (£)"><Input type="number" value={editing.max_order_value} onChange={(e) => setEditing({ ...editing, max_order_value: e.target.value })} /></F>
              <F label="Carrier"><Input value={editing.carrier} onChange={(e) => setEditing({ ...editing, carrier: e.target.value })} placeholder="e.g. Royal Mail" data-testid="rule-field-carrier" /></F>
              <F label="Carrier service"><Input value={editing.carrier_service} onChange={(e) => setEditing({ ...editing, carrier_service: e.target.value })} placeholder="e.g. Tracked 48" data-testid="rule-field-service" /></F>
              <F label="RM service code (e.g. TPS48, SD1)"><Input value={editing.rm_service_code} onChange={(e) => setEditing({ ...editing, rm_service_code: e.target.value })} placeholder="TPS48" data-testid="rule-field-rmcode" /></F>
              <F label="RM service register code"><Input value={editing.rm_service_register_code} onChange={(e) => setEditing({ ...editing, rm_service_register_code: e.target.value })} placeholder="01" data-testid="rule-field-rmreg" /></F>
              <F label="Package format"><Input value={editing.rm_package_format} onChange={(e) => setEditing({ ...editing, rm_package_format: e.target.value })} placeholder="parcel" data-testid="rule-field-rmpkg" /></F>
              <div className="flex items-center gap-2 pt-2"><Switch checked={editing.auto_label} onCheckedChange={(v) => setEditing({ ...editing, auto_label: v })} data-testid="rule-field-autolabel" /><Label>Auto-create label (Phase 3)</Label></div>
              <div className="flex items-center gap-2 pt-2"><Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="rule-field-enabled" /><Label>Enabled</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveRule} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-rule-btn">Save rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
